import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { EntityModel } from '@data-layer/models/entities';
import { enumEntityByKey, enumEntityModelCode } from '@data-layer/enums/entities';
import { LineModel } from '@data-layer/models/entry-summaries';
import { HTS_MASK } from '@helpers/custom-validator';
import { enumUserBetaFlag } from '@app/data-layer/enums/user';
import { CompaniesService } from '@services/companies.service';
import { UserBetaFlagModel } from '@app/data-layer/models/user';
import { BINA_SECOND_GROUP_NUM, BINA_THIRD_GROUP_NUM } from '@shared/bina-mutual/components/const';
import { DESC_ENTITY } from '@constants/desc-entity';
import { TranslateService } from '@ngx-translate/core';
import { ReplaySubject } from 'rxjs';
import { GenericService } from '@services/generic.service';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'desktop-isf-summary-hts-body',
  template: `
    <div class="manufacturer-row" [ngClass]="{ 'is-last': isLastRow, 'is-first': isFirstRow }">
      <div class="manufacturer-row-entity">
        <desktop-summary-entity
          [entity]="manufacturer"
          [isShowIdentifier]="false"
          [entityCode]="enumEntityModelCode.MANUFACTURER"
          [cardTitle]="manufacturerCardTitle"
          [isEditMode]="isEditMode"
          [isCanRemove]="true"
          [isLastInRow]="isLastRow"
          (edit)="edit.emit({ id: manufacturer.id, entityType: enumEntityModelCode.MANUFACTURER, invoiceId: manufacturer.invoiceId })"
          (remove)="deleteMF.emit({ manufacturerId: manufacturer.id, invoiceId: manufacturer.invoiceId })"
        ></desktop-summary-entity>
      </div>

      <div class="manufacturer-row-hts" [ngClass]="{ 'is-editable': isEditMode }">
        <div class="manufacturer-row-hts-header" [ngClass]="{ 'table-view': isHasIsfOcr }">
          <div *ngIf="isHasIsfOcr" class="manufacturer-row-hts-column row-number">#</div>
          <div class="manufacturer-row-hts-column code" [ngClass]="{ 'table-view': isHasIsfOcr }">{{ 'LABELS.HTS_CODE' | translate }}</div>
          <div class="manufacturer-row-hts-column country" [ngClass]="{ 'table-view': isHasIsfOcr }">
            {{ 'LABELS.COUNTRY_OF_ORIGIN' | translate }}
          </div>
          <div class="manufacturer-row-hts-column actions d-print-none" [ngClass]="{ 'table-view': isHasIsfOcr }">
            <button
              type="button"
              class="btn btn-link"
              *ngIf="isEditMode"
              (click)="editHTS.emit({ manufacturerId: manufacturer.id, invoiceId: manufacturer.invoiceId })"
            >
              <svg width="19" height="19" viewBox="0 0 19 19" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M0 14.2525V18.0025H3.75L14.81 6.9425L11.06 3.1925L0 14.2525ZM17.71 4.0425C18.1 3.6525 18.1 3.0225 17.71 2.6325L15.37 0.2925C14.98 -0.0975 14.35 -0.0975 13.96 0.2925L12.13 2.1225L15.88 5.8725L17.71 4.0425Z"
                  fill="currentColor"
                />
              </svg>

              <span *ngIf="!isHasIsfOcr" class="ml-2">{{ 'BUTTON.EDIT' | translate }}</span>
            </button>
          </div>
        </div>

        <div class="manufacturer-row-hts-item" [ngClass]="{ 'table-view': isHasIsfOcr }" *ngFor="let hts of lineItems; let i = index">
          <div *ngIf="isHasIsfOcr" class="manufacturer-row-hts-column row-number">
            <div class="manufacturer-row-hts-column__cell-content">
              {{ i + 1 }}
            </div>
          </div>

          <div *ngIf="isHasIsfOcr" class="manufacturer-row-hts-column code table-view">
            <div class="manufacturer-row-hts-column__cell-content no-horizontal-center">
              <ng-content
                *ngTemplateOutlet="
                  codeGroupItem;
                  context: { lineItem: hts, startingIndex: 0, length: 4, showDot: true, capsulePlaceholderCount: 4 }
                "
              ></ng-content>
              <ng-content
                *ngTemplateOutlet="
                  codeGroupItem;
                  context: { lineItem: hts, startingIndex: BINA_SECOND_GROUP_NUM, showDot: true, capsulePlaceholderCount: 2 }
                "
              ></ng-content>
              <ng-content
                *ngTemplateOutlet="
                  codeGroupItem;
                  context: { lineItem: hts, startingIndex: BINA_THIRD_GROUP_NUM, length: 4, capsulePlaceholderCount: 4 }
                "
              ></ng-content>
            </div>
          </div>
          <div *ngIf="!isHasIsfOcr" class="manufacturer-row-hts-column code">{{ hts?.htsNumber | mask : HTS_MASK }}</div>

          <div class="manufacturer-row-hts-column country" [ngClass]="{ 'table-view': isHasIsfOcr }">
            <div class="manufacturer-row-hts-column__cell-content no-horizontal-center">
              {{ hts?.countryOfOrigin?.description }}
              <div *ngIf="!hts?.countryOfOrigin?.description" class="text-error">
                {{ 'LABELS.MISSING' | translate }}
              </div>
            </div>
          </div>
          <div class="manufacturer-row-hts-column actions d-print-none" [ngClass]="{ 'table-view': isHasIsfOcr }">
            <button type="button" class="btn btn-link" *ngIf="isEditMode" (click)="deleteHTS.emit(hts.id)">
              <i class="fa fa-trash"></i>
            </button>
          </div>
        </div>

        <p class="text-error pt-1" *ngIf="lineItems?.length === 0">{{ 'LABELS.MISSING' | translate }}</p>
      </div>
    </div>

    <ng-template
      #codeGroupItem
      let-lineItem="lineItem"
      let-startingIndex="startingIndex"
      let-length="length"
      let-capsulePlaceholderCount="capsulePlaceholderCount"
      let-showDot="showDot"
    >
      <app-bina-code-item
        [groupNumber]="getCodeGroupAt(lineItem, startingIndex, length)"
        [showDot]="showDot"
        [capsulePlaceholderCount]="capsulePlaceholderCount"
        [isInvalid]="isHtsInvalid(lineItem)"
      ></app-bina-code-item>
    </ng-template>
  `,
  styleUrls: ['./desktop-summary-hts-body.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DesktopSummaryHtsBodyComponent implements OnInit, OnDestroy {
  @Input() public manufacturer: EntityModel;
  @Input() public lineItems: LineModel[];
  @Input() public rowNumber: number;
  @Input() public isShowRowNumber: boolean;
  @Input() public isFirstRow: boolean;
  @Input() public isLastRow: boolean;
  @Input() public isEditMode: boolean;

  @Output() public edit: EventEmitter<{ id: number; entityType: enumEntityModelCode; invoiceId: number }> = new EventEmitter<{
    id: number;
    entityType: enumEntityModelCode;
    invoiceId: number;
  }>();
  @Output() public editHTS: EventEmitter<{ manufacturerId: number; invoiceId: number }> = new EventEmitter<{
    manufacturerId: number;
    invoiceId: number;
  }>();
  @Output() public deleteHTS: EventEmitter<number> = new EventEmitter<number>();
  @Output() public deleteMF: EventEmitter<{ manufacturerId: number; invoiceId: number }> = new EventEmitter<{
    manufacturerId: number;
    invoiceId: number;
  }>();

  public enumEntityModelCode = enumEntityModelCode;
  public enumEntityByKey = enumEntityByKey;
  public HTS_MASK = HTS_MASK;
  public DESC_ENTITY = DESC_ENTITY;
  public BINA_SECOND_GROUP_NUM: number = BINA_SECOND_GROUP_NUM;
  public BINA_THIRD_GROUP_NUM: number = BINA_THIRD_GROUP_NUM;

  private isMobile: boolean;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private cdr: ChangeDetectorRef,
    private genericService: GenericService,
    private translateService: TranslateService,
    private companiesService: CompaniesService,
  ) {}

  public ngOnInit(): void {
    this.genericService.isMobile$.pipe(takeUntil(this.destroy$)).subscribe((isMobile) => {
      this.isMobile = isMobile;
      this.cdr.detectChanges();
    });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public getCodeGroupAt(lineItem: LineModel, startingIndex: number, length: number = 2): string {
    const codeToReturn = lineItem?.htsNumber?.substring(startingIndex, startingIndex + length);
    return (codeToReturn?.length || 0) === length ? codeToReturn : null;
  }

  public isHtsInvalid(lineItem: LineModel): boolean {
    const lineItemValidationMessages = lineItem?.validationMessages || [];
    return !!lineItemValidationMessages.length;
  }

  public get manufacturerCardTitle(): string {
    const mfTitle = DESC_ENTITY[enumEntityModelCode.MANUFACTURER]?.titleDesktop;
    const translatedMfTitle = this.translateService.instant(mfTitle);

    return this.isShowRowNumber ? `${translatedMfTitle} #${this.rowNumber}` : translatedMfTitle;
  }

  public get isHasIsfOcr(): boolean {
    const isWithUserWithIsfOcrFlag = this.companiesService
      .getUser()
      ?.betaFlags?.map((flag: UserBetaFlagModel) => flag.tag)
      .includes(enumUserBetaFlag.isfOcr);

    return isWithUserWithIsfOcrFlag && !this.isMobile;
  }
}
